import axiosInstance from "./axiosInstance";
import { getAuthHeader } from "./permissionService";

export interface Currency {
  currencySymbol?: string;
  value: string;
  currencyId: number;
  currencyCode: string;
  currencyName: string;
}

export interface HeadCurrencyRate {
  headCurrencyId?: number;
  headId: number;
  currencyId: number;
  rate: number;
}

export interface CurrencyHeadFilters {
  pageNo?: number;
  pageSize?: number;
  searchTerm?: string;
}
export interface HeadCurrency {
  id: string;
  publicId?: string;
  currencyId?: string,
  currencyCode: string;
  currencySymbol: string;
  unitRate: string; // 1 unit = X of this currency
}

export interface CurrencyHead {
  id: string;
  publicId?: string;
  currencyHeadMasterId?: string;
  headName: string;
  description: string;
  isActive: boolean;
  isUnit?: boolean; // New field to indicate if this head is a unit
  currencies: HeadCurrency[];
}

export interface CreateCurrencyHeadPayload {
  publicId?: string;
  headName: string;
  description: string;
  isActive: boolean;
  isUnit: boolean;
  createdBy: number;
  updatedBy?: string | number;
}
export interface UpdateCurrencyHeadPayload {
  publicId: string;
  headName: string;
  description: string;
  isActive: boolean;
  isUnit: boolean;
  updatedBy?: string | number;
}

export interface UpdateCurrencyDetailPayload {
  publicId: string;
  currencyId: number;
  unitRate: number;
  updatedBy: number;
}

export interface MumeenInfo {
  name: string;
  phone: string;
  email: string;
  watan: string;
  muminDataId?: number;
}

export interface Receipt {
  receiptNumber: string;
  location: string;
  itsNo: string;
  mumeenInfo: MumeenInfo;
  head: string;
  currency: string;
  unitRate: number;
  units: number;
  amount: number;
  discount: number;
  total: number;
  approved: number;
  createdAt: string;
  userId: string | number;
  userName: string;
}

export type CreateReceiptResponse =
  | { success: boolean; local?: boolean }
  | Receipt;

// Fetch all currencies
export const getAllCurrencies = async (): Promise<Currency[]> => {
  try {
    const response = await axiosInstance.get("/Currency");
    return response.data;
  } catch {
    return [];
  }
};

export const getCurrencyHead = async () => {
  try {
    const response = await axiosInstance.get(`/currency-head`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    return response.data;
  } catch (error) {
    return null;
  }
};

// Fetch head-currency specific rate
export const getHeadCurrencyRate = async (
  headId: number,
  currencyId: number,
): Promise<number> => {
  try {
    const response = await axiosInstance.get(
      `/HeadCurrency/by-head-and-currency`,
      {
        params: { headId, currencyId },
      },
    );

    return typeof response.data?.rate === "number" ? response.data.rate : 0;
  } catch {
    return 0;
  }
};

const USE_BACKEND_API = false;

// Create receipt
export const createReceipt = async (
  data: Receipt,
): Promise<CreateReceiptResponse> => {
  try {
    if (USE_BACKEND_API) {
      const response = await axiosInstance.post("/Receipt", data);
      return response.data as Receipt; // Backend version
    } else {
      const stored = localStorage.getItem("mannatNiyazReceipts");
      let existingReceipts: Receipt[] = [];

      try {
        existingReceipts = stored ? JSON.parse(stored) : [];
      } catch {
        existingReceipts = [];
      }

      existingReceipts.push(data);

      localStorage.setItem(
        "mannatNiyazReceipts",
        JSON.stringify(existingReceipts),
      );

      return { success: true, local: true };
    }
  } catch (error) {
    throw error;
  }
};

// CURRENCY HEAD API START
export const getCurrencyHeadList = async (
  filters: CurrencyHeadFilters = {},
) => {
  try {
    const response = await axiosInstance.get("/currency-head/list", {
      params: {
        pageNo: filters.pageNo ?? 0,
        pageSize: filters.pageSize ?? 20,
        searchText: filters.searchTerm || undefined,
      },
      headers: {
        ...getAuthHeader(),
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createCurrencyHead = async (
  payload: CreateCurrencyHeadPayload,
) => {
  try {
    const response = await axiosInstance.post("/currency-head", payload, {
      headers: {
        ...getAuthHeader(),
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteCurrencyHead = async (
  publicId: string,
  updatedBy: string,
): Promise<void> => {
  await axiosInstance.delete(`/currency-head/${publicId}`, {
    params: {
      updatedBy,
    },
    headers: {
      ...getAuthHeader(),
    },
  });
};


export const UpdateCurrencyHead = async (
  payload: UpdateCurrencyHeadPayload,
) => {
  try {
    const response = await axiosInstance.put(
      `/currency-head/${payload.publicId}`,
      payload,
      {
        headers: {
          ...getAuthHeader(),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};


export const addCurrencyToHead = async (
  headPublicId: string,
  createdBy: number,
  payload: {
    currencyId: number;
    unitRate: number;
  }
) => {
  const response = await axiosInstance.post(
    `/currency-head/${headPublicId}/details`,
    payload,
    {
      params: {
        createdBy,
      },
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeader(),
      },
    }
  );

  return response.data;
};


export const UpdateCurrencyToHead = async (
  payload: UpdateCurrencyDetailPayload,
) => {
  try {
    const response = await axiosInstance.put(
      `/currency-head/details/${payload.publicId}`,
      payload,
      {
        headers: {
          ...getAuthHeader(),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteCurrency = async (
  publicId: string
): Promise<void> => {
  await axiosInstance.delete(
    `/currency-head/details/${publicId}`, // ✅ correct URL
    {
      headers: {
        ...getAuthHeader(),
      },
    }
  );
};
// CURRENCY HEAD API END